/**
 * USD-denominated cost for a Bedrock model invocation — sibling to
 * `aiCredits.ts`, which prices the same event in credits. Priced from the
 * model registry in `aiBedrockModels.ts`; this file only does the USD math.
 *
 * Dollars are always stored and moved as INTEGER micro-dollars (µUSD) —
 * `MICRO_USD_PER_USD` per dollar — mirroring the micro-credit convention so
 * nothing in DynamoDB is a float. `formatMicroUsdAsUsd` converts to a
 * decimal dollar amount for display only.
 */

import log from 'loglevel';

import {
  AI_BEDROCK_MODEL_CATALOG,
  AiBedrockModelCatalogEntry,
  resolveBedrockModelCatalogEntry,
} from './aiBedrockModels';
import {
  AI_EXTERNAL_MODEL_CATALOG,
  AiExternalModelCatalogEntry,
  resolveExternalModelCatalogEntry,
} from './aiExternalModels';

export const MICRO_USD_PER_USD = 1_000_000;

const TOKENS_PER_PRICE_UNIT = 1_000_000;

export interface AiUsdPriceConfig {
  catalog: ReadonlyArray<AiBedrockModelCatalogEntry>;
  /** Non-Bedrock models (e.g. Jev) — tried only when `catalog` has no match. */
  externalCatalog: ReadonlyArray<AiExternalModelCatalogEntry>;
  /** ISO date the price table took effect. */
  effectiveFrom: string;
  version: string;
}

export const DEFAULT_AI_MODEL_PRICES: AiUsdPriceConfig = {
  catalog: AI_BEDROCK_MODEL_CATALOG,
  externalCatalog: AI_EXTERNAL_MODEL_CATALOG,
  effectiveFrom: '2026-09-21T00:00:00.000Z',
  version: 'default',
};

export interface AiUsdCostInput {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  modelId: string;
}

/**
 * Returns the INTEGER micro-dollar cost of one model invocation, or 0 (with a
 * `log.warn`) for a modelId no catalog entry matches — never a guessed
 * price. Rounds (rather than truncates) the final µUSD value, same
 * rationale as `calculateCreditCost`.
 */
export const calculateUsdCost = (
  input: AiUsdCostInput,
  priceTable: AiUsdPriceConfig,
): number => {
  const entry =
    resolveBedrockModelCatalogEntry(input.modelId, priceTable.catalog) ??
    resolveExternalModelCatalogEntry(input.modelId, priceTable.externalCatalog);

  if (!entry) {
    log.warn('calculateUsdCost: unknown model price, billed at $0', {
      modelId: input.modelId,
    });
    return 0;
  }

  const rates = entry.prices;
  const usd =
    (input.inputTokens * rates.inputPricePerMillionUsd +
      input.outputTokens * rates.outputPricePerMillionUsd +
      (input.cacheReadTokens ?? 0) * rates.cacheReadPricePerMillionUsd +
      (input.cacheWriteTokens ?? 0) * rates.cacheWritePricePerMillionUsd) /
    TOKENS_PER_PRICE_UNIT;

  return Math.round(usd * MICRO_USD_PER_USD);
};

/** Micro-dollars → decimal USD, for display only — never fed back into the data layer. */
export const formatMicroUsdAsUsd = (microUsd: number): number =>
  microUsd / MICRO_USD_PER_USD;
